import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, Section, List, Row, Empty, Loading, usePalette, Meta } from "@/components/calm/Calm";
import { humanDate } from "@/components/developer/siteTimelineModel";
import { propertyCopy } from "@/lib/clientTypes";
import { BookMediaLink } from "@/components/listing/BookMedia";
import { hubModel, label, money, HUB_COPY, SEGMENT } from "./propertyOps";

// The desk for property managers, commercial landlords, investors and REITs.
// PM: the unit turnover board across buildings. Commercial: spaces that need
// leasing media. Investor: baseline and watch status. REIT: all of it in one
// portfolio with its pricing tier.
const MAX_VIEWS = 8;

export default function PropertyHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { p, dark } = usePalette();
  const kind = user?.account_type;
  const [port, setPort] = useState(null);
  const [views, setViews] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    api.get("/dev/ops/portfolio").then(async (r) => {
      if (!live) return;
      setPort(r.data || { properties: [] });
      const v = kind === "property_manager" ? "board" : kind === "commercial" ? "leasing" : null;
      if (!v) return;
      const ids = (r.data.properties || []).slice(0, MAX_VIEWS).map((x) => x.project_id);
      const pairs = await Promise.all(ids.map((pid) => api.get(`/dev/projects/${pid}/ops`, { params: { view: v } }).then((x) => [pid, x.data]).catch(() => [pid, null])));
      if (live) setViews(Object.fromEntries(pairs.filter(([, x]) => x)));
    }).catch((e) => { if (live) { setPort({ properties: [] }); setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t load your portfolio."); } });
    return () => { live = false; };
  }, [kind]);

  const copy = propertyCopy(kind);
  const hub = HUB_COPY[kind] || HUB_COPY.investor;
  const m = port ? hubModel(kind, port, views) : null;
  const props = (port && port.properties) || [];
  const open = (pid) => navigate(`/developer/property/${pid}`);

  return (
    <Page testid="property-hub" width={1100}>
      <PageHeader title={hub.title} subtitle={hub.lead}
        action={<div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Btn as={Link} to="/developer/projects" variant="ghost" dark={dark} data-testid="hub-add" style={{ whiteSpace: "nowrap" }}>{`Add ${copy.things.toLowerCase()}`}</Btn>
          <BookMediaLink href={copy.book.href} dark={dark} label={copy.book.label} testId="hub-book" />
        </div>} />
      {error && <div role="alert" style={{ fontSize: 13, color: p.attn }}>{error}</div>}
      {!m ? <Loading /> : (
        <>
          <Stats testid="hub-stats" items={m.stats} />
          {m.tier ? <Meta parts={[m.tier, ...Object.entries(m.segments || {}).map(([k, n]) => `${n} ${SEGMENT[k].toLowerCase()}`)]} /> : null}

          {kind === "property_manager" && (
            <Section title={m.actionTitle} testid="hub-action">
              {!m.action.length ? <Empty>Every unit is up to date.</Empty> : (
                <List>{m.action.map((u) => (
                  <Row stack key={`${u.project_id}-${u.unit}`} testid="hub-unit" onClick={() => open(u.project_id)}
                    title={`${u.property} · Unit ${u.unit}`} meta={[u.status_label, u.last_condition ? `Condition report ${humanDate(u.last_condition.date)}` : "No condition report"]}
                    next={[{ text: u.next_step, attention: true }]} status={label(u.book)} />
                ))}</List>
              )}
            </Section>
          )}

          {kind === "commercial" && (
            <Section title={m.actionTitle} testid="hub-action">
              {!m.action.length ? <Empty>Every space on the market has photos and a floor plan.</Empty> : (
                <List>{m.action.map((x) => (
                  <Row stack key={`${x.project_id}-${x.suite}`} testid="hub-space" onClick={() => open(x.project_id)}
                    title={`${x.property} · Suite ${x.suite}`} meta={[`${(x.sqft || 0).toLocaleString("en-CA")} sq ft`, x.status_label]}
                    next={[{ text: "Needs leasing media.", attention: true }]} status={label(x.book)} />
                ))}</List>
              )}
            </Section>
          )}

          {kind === "investor" && m.action.length > 0 && (
            <Section title={m.actionTitle} testid="hub-action">
              <List>{m.action.map((x) => (
                <Row stack key={x.project_id} onClick={() => open(x.project_id)} title={x.name} meta={[x.address]}
                  next={[{ text: x.next_step, attention: true }]} status={x.overdue ? "Overdue" : "No baseline"} attention />
              ))}</List>
            </Section>
          )}

          <Section title={copy.things} testid="hub-properties">
            {!props.length ? <Empty>{copy.empty}</Empty> : kind === "reit_leasing" ? (
              <List>{(m.reit || []).map((x) => (
                <Row stack key={x.project_id} testid="hub-property" onClick={() => open(x.project_id)} title={x.name}
                  meta={[SEGMENT[x.segment], x.units ? `${x.units} ${x.segment === "commercial" ? "spaces" : "units"}` : null, x.vacant ? `${x.vacant} vacant` : null].filter(Boolean)}
                  next={[x.last_visit ? `Last visit ${humanDate(x.last_visit)}` : "No visits yet", x.spend_ytd_cad_cents ? `${money(x.spend_ytd_cad_cents)} this year` : null].filter(Boolean)}
                  status={x.plan ? "On plan" : ""} />
              ))}</List>
            ) : (
              <List>{props.map((x) => {
                const b = views[x.project_id];
                const meta = kind === "property_manager" && b ? Object.entries(b.counts || {}).map(([k, n]) => `${n} ${k.toLowerCase()}`)
                  : kind === "commercial" && b ? [`${(b.spaces || []).length} spaces`, `${(b.available_sqft || 0).toLocaleString("en-CA")} sq ft available`]
                  : [x.address];
                const next = kind === "investor" ? [{ text: x.next_step, attention: x.overdue }]
                  : [x.last_visit ? `Last visit ${humanDate(x.last_visit.date)}` : "No visits yet"];
                return (
                  <Row stack key={x.project_id} testid="hub-property" onClick={() => open(x.project_id)} title={x.name} meta={meta} next={next}
                    status={kind === "investor" ? (x.overdue ? "Overdue" : x.plan ? "Watched" : x.baseline ? "Baseline on file" : "No baseline") : x.plan ? "On plan" : ""}
                    attention={kind === "investor" && (x.overdue || !x.baseline)} />
                );
              })}</List>
            )}
          </Section>
        </>
      )}
    </Page>
  );
}
